import {
  TokenBalanceResponse,
  TokenSpendResult,
  TokenStreakResult
} from '../types/tokens';

/**
 * Fetch current user's token balance, subscription tier, and recent transactions.
 */
export async function fetchTokenBalance(): Promise<TokenBalanceResponse | null> {
  try {
    const res = await fetch('/api/tokens/balance', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!res.ok) {
      if (res.status === 401) return null; // Unauthenticated guest
      return null;
    }

    const data = await res.json() as TokenBalanceResponse;
    return data;
  } catch (err) {
    console.warn('[tokenService] fetchTokenBalance notice:', err);
    return null;
  }
}

/**
 * Atomically spend tokens on high-compute actions (YouTube slicing, Demucs isolation, clean exports).
 */
export async function spendTokens(
  action: string,
  amount = 1,
  referenceId?: string,
  description?: string
): Promise<TokenSpendResult> {
  try {
    const res = await fetch('/api/tokens/spend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, amount, referenceId, description })
    });

    const data = await res.json() as TokenSpendResult;
    if (res.ok && data.success) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('soulsync:balance_updated'));
      }
    }
    return data;
  } catch (err: any) {
    console.error('[tokenService] spendTokens error:', err);
    return {
      success: false,
      error: err.message || 'Failed to communicate with token spend service'
    };
  }
}

/**
 * Claim +1 Free Daily Streak token.
 */
export async function claimDailyStreak(): Promise<TokenStreakResult> {
  try {
    const res = await fetch('/api/tokens/claim-streak', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    const data = await res.json() as TokenStreakResult;
    if (res.ok && data.success) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('soulsync:balance_updated'));
      }
    }
    return data;
  } catch (err: any) {
    console.error('[tokenService] claimDailyStreak error:', err);
    return {
      success: false,
      error: err.message || 'Failed to claim daily streak'
    };
  }
}

/**
 * Initiate Stripe Checkout for a Token Pack or Creator Pro subscription.
 */
export async function createCheckoutSession(params: {
  packId?: string;
  tier?: string;
}): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    const res = await fetch('/api/billing/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });

    const data = await res.json() as any;
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Failed to start checkout' };
    }

    return { success: true, url: data.url };
  } catch (err: any) {
    console.error('[tokenService] createCheckoutSession error:', err);
    return { success: false, error: err.message || 'Network error initiating checkout' };
  }
}

/**
 * Open self-service Stripe Customer Portal.
 */
export async function createCustomerPortalSession(): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    const res = await fetch('/api/billing/portal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    const data = await res.json() as any;
    if (!res.ok || !data.success) {
      return { success: false, error: data.error || 'Failed to open customer portal' };
    }

    return { success: true, url: data.url };
  } catch (err: any) {
    console.error('[tokenService] createCustomerPortalSession error:', err);
    return { success: false, error: err.message || 'Network error opening portal' };
  }
}
